/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

"use client";

import { css, cva, cx } from "@panda/css";
import { Box } from "@panda/jsx";
import { useCurrentSearchQuery, useDirectoryConfig, useDirectoryItems } from "../core/Directory.context";
import type { DirectoryGridRenderProp } from "../core/Directory.types";
import { DirectoryNoResults } from "./DirectoryNoResults.element";

export type DirectoryGridColumns = 1 | 2 | 3 | 4;

interface DirectoryGridCommonProps<T> {
	children: DirectoryGridRenderProp<T>;
	/** Extra Panda CSS class. Use this to override column layout, spacing, or any other style. */
	className?: string;
	/**
	 * Rendered when the filtered result set is empty. Overrides the default
	 * `DirectoryNoResults`. Receives the current search query (or `""` if no
	 * `search` filter is configured / nothing typed) so callers can render
	 * domain-specific empty-state copy without re-deriving the query from
	 * context.
	 */
	renderEmpty?: (query: string) => React.ReactNode;
}

/**
 * Discriminated layout — pass `columns` OR `minCardWidth`, never both. TS
 * enforces it at the prop level so a caller passing both gets a compile
 * error rather than silent column-wins behavior.
 */
export type DirectoryGridProps<T> = DirectoryGridCommonProps<T> &
	(
		| {
				/**
				 * Fixed column count (responsive: drops to fewer columns at lower
				 * breakpoints — see comment on `gridColumnsRecipe`). Matches the
				 * canonical Cosmos card-section pattern (BlogCards / FeatureCards
				 * / NewsCards).
				 */
				columns: DirectoryGridColumns;
				minCardWidth?: never;
		  }
		| {
				columns?: never;
				/**
				 * Minimum card width. The grid uses `grid-template-columns:
				 * repeat(auto-fit, minmax(<min>, 1fr))` so columns flow without
				 * caller-provided breakpoints. Defaults to "260px" when neither
				 * `columns` nor `minCardWidth` is set.
				 */
				minCardWidth?: string;
		  }
	);

const gridBaseStyles = css({
	display: "grid",
	width: "full",
	gap: "eds.dimension.24",
	// Reset the default browser list bullet — we use `role="list"` for AT
	// semantics, not visual style.
	listStyle: "none",
	margin: "0",
	padding: "0",
});

/**
 * Token-based responsive column layout.
 *
 * Cosmos Panda breakpoints (smallest → largest): xxs 0, xs 375, sm 768,
 * md 1024, lg 1280, xl 1440. Panda's `<bp>Down` matches "at or below" — so
 * `mdDown` covers anything ≤1024 (which includes tablet widths we want at
 * 2-col). Use `smDown` to drop to single column only at mobile (≤768).
 *
 * Card content (logo + name + meta + tags) needs ~340px to render
 * comfortably; the intermediate `lgDown` step gives 2 columns at tablet
 * widths (768–1279) before collapsing to a single column on mobile.
 *
 * - Desktop  (≥1280, lg+):     full requested count
 * - Tablet   (768–1279, smOnly..lgDown): 2 cols (or 1 for the 1-col variant)
 * - Mobile   (≤768, smDown):   1 col
 */
// Panda's grid-template-columns tokens (`"1"`, `"2"`, …) are responsive-
// aware — they generate `.lgDown\:grid-tc_2` / `.smDown\:grid-tc_1` media-
// query variants automatically. Arbitrary strings like `"1fr 1fr 1fr"`
// generate flat classes with no media-query wrapping, which silently breaks
// responsive layouts. Stay on tokens.
const gridColumnsRecipe = cva({
	base: {},
	variants: {
		columns: {
			1: { gridTemplateColumns: "1" },
			2: { gridTemplateColumns: { base: "2", smDown: "1" } },
			3: { gridTemplateColumns: { base: "3", lgDown: "2", smDown: "1" } },
			4: { gridTemplateColumns: { base: "4", lgDown: "2", smDown: "1" } },
		},
	},
});

export function DirectoryGrid<T>(props: DirectoryGridProps<T>): React.JSX.Element {
	const { children, className, renderEmpty } = props;
	const columns = "columns" in props ? props.columns : undefined;
	const minCardWidth = "minCardWidth" in props ? (props.minCardWidth ?? "260px") : "260px";

	const config = useDirectoryConfig<T>();
	const result = useDirectoryItems<T>();
	const currentQuery = useCurrentSearchQuery();
	const items = result.visibleItems;
	const getKey = config.getKey;

	if (items.length === 0) {
		if (renderEmpty) return <>{renderEmpty(currentQuery)}</>;
		return <DirectoryNoResults />;
	}

	// `columns` mode uses Panda CVA tokens (canonical Cosmos card pattern).
	// `minCardWidth` mode uses inline auto-fit (preserved for callers that
	// want a fully fluid layout without caller-provided breakpoints).
	//
	// `role="list"` on the grid + `role="listitem"` on each wrapper gives AT
	// users a recognizable rotor target ("list with N items") instead of an
	// opaque div with N children. Combined with the page-context live region
	// from `Directory.molecule`, pagination is fully announced.
	if (columns) {
		return (
			<Box role="list" className={cx(gridBaseStyles, gridColumnsRecipe({ columns }), className)}>
				{items.map((item, index) => (
					<DirectoryGridItem key={getKey(item)} item={item} index={index} render={children} />
				))}
			</Box>
		);
	}

	return (
		<Box
			role="list"
			className={cx(gridBaseStyles, className)}
			// Inline style carries the dynamic gridTemplateColumns that Panda cannot resolve
			// at build time — this lets callers pick any minimum card width without forcing
			// a pre-enumerated preset set. `gap` stays in the Panda class so it uses tokens.
			style={{ gridTemplateColumns: `repeat(auto-fit, minmax(${minCardWidth}, 1fr))` }}
		>
			{items.map((item, index) => (
				<DirectoryGridItem key={getKey(item)} item={item} index={index} render={children} />
			))}
		</Box>
	);
}

function DirectoryGridItem<T>({
	item,
	index,
	render,
}: {
	item: T;
	index: number;
	render: DirectoryGridRenderProp<T>;
}): React.JSX.Element {
	return <div role="listitem">{render(item, index)}</div>;
}
